import { useEffect, useCallback } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Header } from "../components/shared/Header";
import { BottomNav, type TabId } from "../components/shared/BottomNav";
import { ParticipantsTab } from "./ParticipantsTab";
import { ExpensesTab } from "./ExpensesTab";
import { SettlementTab } from "./SettlementTab";
import { ShareButton } from "../components/settlement/ShareButton";
import { Skeleton } from "../components/ui/skeleton";
import { NotFoundPage } from "./NotFoundPage";
import { useEvents } from "../context/EventContext";
import { useViewTransition } from "../hooks/useViewTransition";

const renderTab = {
  participants: <ParticipantsTab />,
  expenses: <ExpensesTab />,
  settlement: <SettlementTab />,
};

const validTabs: TabId[] = ["participants", "expenses", "settlement"];

export function EventDetailPage() {
  const { eventId, tab } = useParams<{ eventId: string; tab: string }>();
  const navigate = useNavigate();
  const { setCurrentEventId, currentEvent, isLoading, events, saveStatus } =
    useEvents();
  const { startTransition } = useViewTransition();

  // Validate tab parameter
  const activeTab: TabId = validTabs.includes(tab as TabId)
    ? (tab as TabId)
    : "participants";

  const handleBack = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const handleTabChange = useCallback(
    (newTab: TabId) => {
      if (newTab === activeTab) return;
      startTransition(() => {
        navigate(`/event/${eventId}/${newTab}`, { replace: true });
      });
    },
    [activeTab, eventId, navigate, startTransition]
  );

  useEffect(() => {
    if (eventId) {
      setCurrentEventId(eventId);
    }
    return () => setCurrentEventId(null);
  }, [eventId, setCurrentEventId]);

  // Redirect to valid tab if invalid tab in URL
  if (tab && !validTabs.includes(tab as TabId)) {
    return <Navigate to={`/event/${eventId}/participants`} replace />;
  }

  // Show 404 if event not found (after loading)
  if (!isLoading && eventId && !events.find((e) => e.id === eventId)) {
    return <NotFoundPage />;
  }

  if (isLoading || !currentEvent) {
    return (
      <div className="min-h-screen bg-background pb-16">
        <Header title="" onBack={handleBack} />
        <div className="p-4 space-y-4">
          <Skeleton className="h-6 w-48" />
          <div className="space-y-3">
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
        </div>
        <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <Header
        title={currentEvent.name}
        onBack={handleBack}
        action={activeTab === "settlement" ? <ShareButton /> : undefined}
        saveStatus={saveStatus}
        variant="prominent"
      />
      <main style={{ viewTransitionName: "tab-content" }}>
        {renderTab[activeTab]}
      </main>
      <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
}
